import { apiGet, apiPut } from "../api-client"

// ---- Business Profile ----

export interface BusinessProfile {
  data: Record<string, any>
  created_at: string
  updated_at: string
}

export interface BusinessProfileSchema {
  required: string[]
  optional: string[]
}

/**
 * Fetch the tenant's business profile (admin-only).
 */
export async function getBusinessProfile(): Promise<BusinessProfile> {
  return apiGet<BusinessProfile>("/api/tenant/business-profile/")
}

/**
 * Deep-merge a partial profile into the tenant's business profile (admin-only).
 * The backend deep-merges the request body into the existing `data` object.
 */
export async function putBusinessProfile(
  partial: Record<string, any>
): Promise<BusinessProfile> {
  return apiPut<BusinessProfile>("/api/tenant/business-profile/", partial)
}

/**
 * Fetch the schema (required/optional dotted paths) for a given agency+form.
 */
export async function getBusinessProfileSchema(
  agency: string,
  form: string
): Promise<BusinessProfileSchema> {
  const params = new URLSearchParams({ agency, form })
  return apiGet<BusinessProfileSchema>(
    `/api/tenant/business-profile/schema/?${params.toString()}`
  )
}

// ---- Pure utilities ----

/**
 * Walks a dotted path on an object, returning the value at the path or
 * `undefined` if any segment is missing.
 *
 * @example
 *   getDottedPath({ rrc: { w3a: { foo: "x" } } }, "rrc.w3a.foo") // "x"
 *   getDottedPath({}, "rrc.w3a.foo") // undefined
 */
export function getDottedPath(obj: any, path: string): any {
  if (obj == null || !path) return undefined
  const segments = path.split(".")
  let current: any = obj
  for (const segment of segments) {
    if (current == null || typeof current !== "object") return undefined
    current = current[segment]
  }
  return current
}

/**
 * Expands a flat object of dotted-path keys into a nested object.
 *
 * @example
 *   expandDottedKeys({ "rrc.w3a.foo": "x" })
 *   // => { rrc: { w3a: { foo: "x" } } }
 */
export function expandDottedKeys(
  flat: Record<string, string>
): Record<string, any> {
  const result: Record<string, any> = {}
  for (const [path, value] of Object.entries(flat)) {
    const segments = path.split(".")
    let cursor: Record<string, any> = result
    for (let i = 0; i < segments.length - 1; i++) {
      const segment = segments[i]
      if (cursor[segment] == null || typeof cursor[segment] !== "object") {
        cursor[segment] = {}
      }
      cursor = cursor[segment]
    }
    cursor[segments[segments.length - 1]] = value
  }
  return result
}
